import type { HTMLAttributes, ReactNode } from 'react';

import type { OptionExtra } from '../../lib/option';
import type { PrimativeFieldProps, PrimativeFieldPropsOmit, PrimativeFormPropsOmit, PrimitiveValueType } from '../../lib/utils';
import type { AnyCheckboxGroupRenderItem, CheckboxGroupOption, CheckboxGroupRenderItem } from '../form/checkbox-group';

import { useFieldContext } from '../../hooks/use-form-context';
import { CheckboxGroupItems, CheckboxGroupTrigger, CheckboxGroup as PrimativeCheckBoxGroup } from '../form/checkbox-group';

type InferValueFromOptions<TOptions extends readonly CheckboxGroupOption<PrimitiveValueType, OptionExtra | undefined>[]> = TOptions[number] extends CheckboxGroupOption<infer Value, any> ? Value : never;
type InferExtraFromOptions<TOptions extends readonly CheckboxGroupOption<PrimitiveValueType, OptionExtra | undefined>[]> = TOptions[number] extends CheckboxGroupOption<any, infer Extra> ? Extra : never;

type NonAsChildCheckboxGroupProps<Value extends PrimitiveValueType, Extra extends OptionExtra | undefined = undefined, RenderItem extends CheckboxGroupRenderItem<Value, Extra> = AnyCheckboxGroupRenderItem> = PrimativeFieldProps<Value[]> & {
    asChild?: false;
    options: CheckboxGroupOption<Value, Extra>[];
    orientation?: 'horizontal' | 'vertical';
    renderItem?: RenderItem;
    className?: string;
    children?: ReactNode;
} & Omit<HTMLAttributes<HTMLDivElement>, PrimativeFormPropsOmit>;

export type CheckboxGroupProps<TOptions extends readonly CheckboxGroupOption<PrimitiveValueType, OptionExtra | undefined>[] = CheckboxGroupOption<PrimitiveValueType, OptionExtra | undefined>[], Value extends PrimitiveValueType = InferValueFromOptions<TOptions>, Extra extends OptionExtra | undefined = InferExtraFromOptions<TOptions>, RenderItem extends CheckboxGroupRenderItem<Value, Extra> = CheckboxGroupRenderItem<Value, Extra>> = Omit<NonAsChildCheckboxGroupProps<Value, Extra, RenderItem>, 'id' | 'name' | 'value' | 'onChange' | 'onBlur'> & {
    options: TOptions;
    renderItem?: RenderItem;
};

export function CheckboxGroup<TOptions extends readonly CheckboxGroupOption<PrimitiveValueType, OptionExtra | undefined>[]>(props: CheckboxGroupProps<TOptions>) {
    type Value = InferValueFromOptions<TOptions>;
    const { children, ...restProps } = props;

    const field = useFieldContext<Value[] | null>();

    return (
        <PrimativeCheckBoxGroup
            id={field.name}
            name={field.name}
            value={field.state.value ?? []}
            onChange={field.handleChange}
            onBlur={field.handleBlur}
            children={children || <CheckboxGroupItems />}
            {...restProps}
        />
    );
}

CheckboxGroup.displayName = 'CheckboxGroup';

export type CheckboxGroupFieldProps<TValue extends PrimitiveValueType, Extra extends OptionExtra | undefined = undefined, RenderItem extends CheckboxGroupRenderItem<TValue, Extra> = AnyCheckboxGroupRenderItem> = Omit<NonAsChildCheckboxGroupProps<TValue, Extra, RenderItem>, PrimativeFieldPropsOmit | 'options'> & {
    options: CheckboxGroupOption<TValue, Extra>[];
};

CheckboxGroup.Items = CheckboxGroupItems;
CheckboxGroup.Trigger = CheckboxGroupTrigger;
